import prismatic from '@prismatic-io/embedded';

export interface PrismaticAuthResponse {
  token: string;
  enabledFeatures: string[];
}

// Simple in-memory cache for auth tokens
const authCache = new Map<string, { token: string; timestamp: number }>();
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Export for testing purposes
export const clearAuthCache = () => authCache.clear();

// API function - only handles the HTTP request
export const authenticatePrismaticAPI = async (
  organizationId: string
): Promise<PrismaticAuthResponse> => {
  // Get the access token from sessionStorage
  const accessToken = window.sessionStorage.getItem('accessToken');

  if (!accessToken) {
    throw new Error('No access token found');
  }

  // Get the environment variables
  const { api2BaseUrl } = window.envVars;

  if (!api2BaseUrl) {
    throw new Error('api2BaseUrl not found');
  }

  // Fetch the Prismatic authentication token
  const response = await fetch(
    `${api2BaseUrl}/org/integrations/prismatic/authenticate?organization_id=${organizationId}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch Prismatic token: ${response.statusText}`);
  }

  const data = await response.json();
  const { token, enabled_features: enabledFeaturesFromApi } = data;

  return {
    token,
    enabledFeatures: enabledFeaturesFromApi || ['marketplace']
  };
};

// Wrapper that includes caching
export const authenticatePrismatic = async (organizationId: string) => {
  // Check in-memory cache first
  const cached = authCache.get(organizationId);
  const now = Date.now();

  if (cached && now - cached.timestamp < CACHE_DURATION) {
    // Use cached token
    await prismatic.authenticate({ token: cached.token });
    return cached.token;
  }

  // Otherwise fetch fresh data
  const authData = await authenticatePrismaticAPI(organizationId);

  // Update cache
  authCache.set(organizationId, {
    token: authData.token,
    timestamp: now
  });

  // Authenticate with Prismatic SDK
  await prismatic.authenticate({ token: authData.token });

  return authData.token;
};

export const showPrismatic = async (
  organizationId: string,
  selector: string
) => {
  await authenticatePrismatic(organizationId);

  prismatic.showMarketplace({
    selector,
    theme: 'LIGHT'
  });
};

export const showWorkflows = async (
  organizationId: string,
  selector: string
) => {
  await authenticatePrismatic(organizationId);

  prismatic.showWorkflows({
    selector,
    theme: 'LIGHT'
  });
};

export const showLogs = async (organizationId: string, selector: string) => {
  await authenticatePrismatic(organizationId);

  prismatic.showLogs({
    selector,
    theme: 'LIGHT'
  });
};
